import { useEffect } from 'react';
import { Avatar, Card, Col, Row, Space, Tag } from 'antd';
import { DeleteOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmAction, Datagrid, TooltipButton } from 'components';
import Actions from 'components/Actions/Actions';
import { globalActions } from 'app/services/global';
import { Drawers } from 'app/types/drawer';
import { Worker } from 'app/types/worker';
import { useDeleteWorkerMutation } from 'app/services/workers';
import { useHasPermission } from 'hooks/authorization/useHasPermission';
import { Paths, User } from 'constants/index';

type DetailsProps = {
  worker: Worker;
};

const Details = ({ worker }: DetailsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [ deleteWorker, deleteMeta ] = useDeleteWorkerMutation();
  const canManageWorkers = useHasPermission(User.Permissions.MANAGE_WORKERS);

  useEffect(() => {
    if (deleteMeta.isSuccess) navigate(Paths.WORKERS_ACTIVE);
  }, [ deleteMeta.isSuccess ]);

  const handleEdit = () => {
    dispatch(globalActions.openDrawer({ key: Drawers.EDIT_WORKER, props: { worker } }));
  };

  const content = `"${worker.FullName}"`;
  const actions = [];

  if (canManageWorkers) {
    actions.push(
      <TooltipButton
        key="edit"
        title={t('common.edit')}
        buttonProps={{ onClick: handleEdit, icon: <EditOutlined />, type: 'link' }}
      />,
      <ConfirmAction
        key="delete"
        callback={() => deleteWorker(worker.Id)}
        confirmMessage={t('common.deleteconfirm', { content })}
        successMessage={t('common.deletesuccess', { content })}
        tooltipButtonProps={{
          buttonProps: { icon: <DeleteOutlined />, type: 'link', danger: true },
          title: t('common.delete'),
        }}
        meta={deleteMeta}
      />,
    );
  }

  const renderActiveTag = () => {
    if (worker.IsActive) return <Tag color={'success'}>{t('common.active')}</Tag>;
    return <Tag color={'error'}>{t('common.inactive')}</Tag>;
  };

  const header = (
    <Row justify="space-between">
      <Col>
        <Space>
          <Avatar size={64} src={worker.PhotoUrl} icon={<UserOutlined />} />
          <div className="title">{worker.FullName}</div>
          {renderActiveTag()}
          {worker.HasExpiredDocuments && <Tag color={'error'}>{t('entities.worker.hasexpireddocuments')}</Tag>}
        </Space>
      </Col>
      <Col>
        <Actions actions={actions} />
      </Col>
    </Row>
  );

  return (
    <Card title={header}>
      <Datagrid
        data={worker}
        groupedKeys={[
          {
            keys: [
              'LastName',
              'FirstName',
              'JobTitle',
              'EmployerName',
              'EmployerRegistrationCode',
              'TaxNumber',
              'DateOfBirth',
              'PersonalIdentityCode',
            ],
          },
        ]}
        translationsNamespace="entities.worker"
        dateOnlyKeys={[ 'DateOfBirth' ]}
      />
    </Card>
  );
};

export default Details;
